import { useQuery } from 'react-query'

import { handleException } from 'clients/exception-handling'
import type { FieldCatalogResponse } from 'features/charts-v2/types/yogiql'
import { errorMessage, warnYogiQLFallback } from 'features/charts-v2/utils'
import type { FilterService } from 'features/filters/service'
import type { FilterOption } from 'features/filters/types'
import {
  type FeatureFlagsResponse,
  useFeatureFlags,
} from 'features/project/feature-flags'
import { projectFilterListKey } from 'src/hooks/use-project-filter-list'

// The backend's ENHANCED_DIALECT_FLAG; an existing hub flag, default off.
const ENHANCED_DIALECT_FLAG = 'enhanced_dialect'

/** Where filter-option counts come from: wait, the legacy route, or YogiQL. */
export type YogiQLFilterContext =
  | { mode: 'pending' }
  | { mode: 'legacy' }
  | {
      mode: 'yogiql'
      catalog: FieldCatalogResponse
      hubOptions: FilterOption[] | undefined
    }

/**
 * Under the hub's `enhanced_dialect` flag, the field catalog the picker's
 * YogiQL queries encode against, and the hub's names-only options (read from
 * the project root's query, never another hub's). Pending until the flag and
 * catalog settle, and while the hub's options are on their way; legacy when
 * the flag is off or the catalog fails to load.
 */
export function useYogiQLFilterContext(
  projectId: string | undefined,
  filterService: FilterService,
): YogiQLFilterContext {
  const { data: flags, isLoading: flagsLoading } = useFeatureFlags()
  const enabled =
    (flags as FeatureFlagsResponse | undefined)?.[ENHANCED_DIALECT_FLAG] ===
    true
  const { data: catalog, status } = useQuery(
    ['filters-yogiql-field-catalog', projectId],
    () => filterService.getFieldCatalog(projectId ?? ''),
    {
      meta: { operation: 'filters.field-catalog.read' },
      enabled: enabled && !!projectId,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: 2,
      onError: (error) => {
        handleException(error, {
          operation: 'filters.field-catalog.read',
          disposition: 'delegated',
          owner: 'QueryCache',
        })
        return warnYogiQLFallback(
          `field-catalog request failed, filter options use the legacy route: ${errorMessage(error)}`,
        )
      },
    },
  )
  // The project root fetches these; a count built before they land would lack
  // their labels and zero-count values, so wait for them unless they failed.
  const { data: hubOptions, status: hubOptionsStatus } = useQuery<
    FilterOption[]
  >(projectFilterListKey(projectId ?? ''), { enabled: false })
  if (flagsLoading) return { mode: 'pending' }
  if (!enabled) return { mode: 'legacy' }
  if (status === 'loading' || status === 'idle') return { mode: 'pending' }
  if (!catalog) return { mode: 'legacy' }
  if (hubOptionsStatus === 'loading' || hubOptionsStatus === 'idle') {
    return { mode: 'pending' }
  }
  return { mode: 'yogiql', catalog, hubOptions }
}
